import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { logger } from '@repo/shared-utils';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console and logger utility (which sends to PostHog)
    logger.error("Uncaught error:", error, errorInfo);
    
    // Also try to capture directly with PostHog if available
    try {
      if (typeof window !== 'undefined') {
        // PostHog may be added to window by the PostHog script
        interface WindowWithPostHog extends Window {
          posthog?: {
            captureException?: (error: Error, options?: Record<string, unknown>) => void;
          };
        }
        const posthog = (window as WindowWithPostHog).posthog;
        if (posthog && typeof posthog.captureException === 'function') {
          posthog.captureException(error, {
            componentStack: errorInfo.componentStack,
            errorBoundary: true,
            errorInfo: errorInfo.toString(),
          });
        }
      }
    } catch {
      // Silently fail if PostHog capture fails
    }
  }

  private handleReset = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle size={32} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Something went wrong</h1>
            <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm">
              The application encountered an unexpected error. This is often caused by stale data or environment restrictions.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg text-xs font-mono text-slate-600 dark:text-slate-300 mb-6 text-left overflow-auto max-h-32">
              {this.state.error?.message}
            </div>
            <button 
              onClick={this.handleReset}
              className="w-full flex items-center justify-center bg-brand-600 hover:bg-brand-700 text-white py-3 rounded-xl font-bold transition shadow-md"
            >
              <RefreshCw size={18} className="mr-2" /> Clear Data & Restart
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
